<template>
  <!--
    Body of the PMAs tab in the EditCompany modal. Lists every distinct
    FAA `make` represented in this Company's linked PMAs, grouped as
    accordions. PMAs are lazy-loaded per make when the accordion opens.

    `total` and `noMakeCount` come from the backend so the header is the
    true M2M-linked total — independent of whether every PMA happens to
    have a `make` value. When some don't, an extra "(no make)" accordion
    surfaces them so the count adds up.
  -->
  <div class="py-2">
    <div v-if="loading" class="text-xs text-gray-600 py-2">
      Loading makes…
    </div>
    <div v-else-if="total === 0" class="text-xs text-gray-600 py-2">
      No PMAs are linked to this company.
    </div>
    <div v-else>
      <div class="text-xs text-gray-600 pb-2">
        {{ total }} PMA<span v-if="total !== 1">s</span>
        across {{ makes.length }} make<span v-if="makes.length !== 1">s</span>
        <span v-if="noMakeCount > 0">
          ({{ noMakeCount }} without a make)
        </span>
      </div>
      <div class="divide-y divide-gray-200 border border-gray-200 rounded">
        <CompanyPMAMakeRow
          v-for="make in makes"
          :key="make.make"
          :make="make"
          :companyId="company.id"
        />
        <CompanyPMAMakeRow
          v-if="noMakeCount > 0"
          :make="{ make: null, pma_count: noMakeCount }"
          :companyId="company.id"
          :isNoMake="true"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { defineProps, ref, onMounted } from "vue";
import { useStore } from "vuex";
import { getCompanyPMAMakes } from "@/services/Company";
import CompanyPMAMakeRow from "./CompanyPMAMakeRow.vue";

const props = defineProps({
  company: { type: Object, required: true },
});
const store = useStore();

const makes = ref([]);
const noMakeCount = ref(0);
const total = ref(0);
const loading = ref(false);

async function loadMakes() {
  loading.value = true;
  try {
    const response = await getCompanyPMAMakes({
      token: store.state.auth.token,
      companyId: props.company.id,
    });
    makes.value = response.makes;
    noMakeCount.value = response.noMakeCount;
    total.value = response.total;
  } catch (err) {
    console.error("Failed to load PMA makes for company", props.company.id, err);
    makes.value = [];
    noMakeCount.value = 0;
    total.value = 0;
  } finally {
    loading.value = false;
  }
}

onMounted(loadMakes);
</script>
